import React from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, ArrowLeft } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const ConsentimientoPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-teal-600 py-12">
        <div className="container-max mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-teal-100 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
          <Badge className="bg-white/20 text-white hover:bg-white/20 mb-4">
            <FileCheck className="w-3 h-3 mr-1" />
            Legal
          </Badge>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-white">
            Consentimiento Informado
          </h1>
          <p className="text-teal-100 mt-2">Para servicios de telepsicología</p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-max mx-auto">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-soft p-8 md:p-12">
            <div className="prose prose-slate max-w-none">
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-8">
                <p className="text-teal-800 font-medium mb-0">
                  Al agendar una sesión en AnonimaMente, declaras haber leído, comprendido y aceptado 
                  este consentimiento informado para recibir servicios de telepsicología.
                </p>
              </div>

              <h2>1. ¿Qué es la telepsicología?</h2>
              <p>
                La telepsicología es la prestación de servicios psicológicos a través de tecnologías 
                de comunicación a distancia, como videollamadas. Permite acceder a atención psicológica 
                profesional sin necesidad de desplazamiento físico.
              </p>

              <h2>2. Naturaleza del servicio</h2>
              <p>
                Las sesiones en AnonimaMente son realizadas por psicólogos titulados y con registro 
                profesional verificado. El servicio incluye:
              </p>
              <ul>
                <li>Evaluación psicológica</li>
                <li>Orientación y consejería</li>
                <li>Intervención psicoterapéutica</li>
                <li>Psicoeducación</li>
              </ul>

              <h2>3. Anonimato y seudónimo</h2>
              <p>
                Entiendo y acepto que:
              </p>
              <ul>
                <li>Me identificaré ante mi psicólogo usando un <strong>seudónimo</strong> (nombre ficticio)</li>
                <li>No estoy obligado/a a revelar mi identidad real</li>
                <li>Puedo mantener mi <strong>cámara apagada</strong> durante las sesiones si lo prefiero</li>
                <li>El profesional respetará mi decisión de mantener el anonimato</li>
              </ul>

              <h2>4. Limitaciones del servicio</h2>
              <p>
                Comprendo que la telepsicología tiene ciertas limitaciones:
              </p>
              <ul>
                <li>Puede haber interrupciones técnicas (conexión, audio, video)</li>
                <li>No permite evaluaciones que requieran presencia física</li>
                <li>No es apropiada para todas las condiciones clínicas</li>
                <li>El profesional puede recomendar atención presencial si lo considera necesario</li>
              </ul>

              <h2>5. NO es servicio de emergencias</h2>
              <p className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <strong>IMPORTANTE:</strong> AnonimaMente NO es un servicio de emergencias ni crisis. 
                Si experimento una crisis de salud mental, ideas suicidas, autolesión, o situación de 
                riesgo vital, debo contactar inmediatamente:
              </p>
              <ul>
                <li><strong>Salud Responde:</strong> 600 360 7777</li>
                <li><strong>Fono Ayuda:</strong> *4141</li>
                <li><strong>Urgencias</strong> del centro de salud más cercano</li>
              </ul>

              <h2>6. Confidencialidad</h2>
              <p>
                El profesional está obligado a mantener la confidencialidad de todo lo conversado 
                en sesión, con las siguientes excepciones legales:
              </p>
              <ul>
                <li>Riesgo inminente de daño para mí o terceros</li>
                <li>Sospecha de abuso a menores o personas vulnerables</li>
                <li>Requerimiento judicial</li>
              </ul>
              <p>
                En caso de necesitar romper la confidencialidad, el profesional me informará 
                (excepto cuando la ley lo prohíba expresamente).
              </p>

              <h2>7. No grabación</h2>
              <p>
                Las sesiones de video <strong>NO se graban</strong> por parte de la plataforma ni 
                del profesional. Me comprometo a no grabar las sesiones sin consentimiento mutuo.
              </p>

              <h2>8. Responsabilidad del usuario</h2>
              <p>
                Me comprometo a:
              </p>
              <ul>
                <li>Proporcionar información veraz sobre mi situación (aunque no mi identidad)</li>
                <li>Conectarme desde un lugar privado y seguro</li>
                <li>Informar si mi situación cambia significativamente</li>
                <li>Respetar los horarios acordados</li>
                <li>Comunicar si necesito derivación a otro profesional o servicio</li>
              </ul>

              <h2>9. Derivaciones</h2>
              <p>
                Entiendo que mi psicólogo puede sugerir:
              </p>
              <ul>
                <li>Evaluación presencial si lo considera necesario</li>
                <li>Evaluación psiquiátrica si sospecha necesidad de medicación</li>
                <li>Otros servicios especializados según mi situación</li>
              </ul>
              <p>
                Las derivaciones son recomendaciones profesionales que puedo aceptar o no, 
                asumiendo las consecuencias de mi decisión.
              </p>

              <h2>10. Medicamentos</h2>
              <p>
                Los psicólogos <strong>NO prescriben medicamentos</strong>. Si requiero evaluación 
                para medicación, debo consultar con un psiquiatra.
              </p>

              <h2>11. Cancelaciones</h2>
              <p>
                Puedo cancelar o reagendar mi cita con al menos [X] horas de anticipación. 
                Las cancelaciones tardías pueden estar sujetas a cobro según la política del profesional.
              </p>

              <h2>12. Derechos del usuario</h2>
              <p>
                Tengo derecho a:
              </p>
              <ul>
                <li>Recibir información clara sobre el proceso terapéutico</li>
                <li>Hacer preguntas en cualquier momento</li>
                <li>Cambiar de profesional si lo deseo</li>
                <li>Interrumpir el proceso cuando lo considere</li>
                <li>Presentar reclamos si no estoy satisfecho/a</li>
              </ul>

              <h2>13. Aceptación</h2>
              <p>
                Al marcar la casilla de aceptación durante el proceso de reserva, declaro que:
              </p>
              <ul>
                <li>He leído y comprendido este consentimiento informado</li>
                <li>Acepto las condiciones aquí descritas</li>
                <li>Entiendo las limitaciones del servicio</li>
                <li>Soy mayor de 18 años o cuento con autorización de mi tutor legal</li>
              </ul>

              <hr className="my-8" />

              <p className="text-sm text-slate-500">
                Si tienes dudas sobre este consentimiento, contacta a [email@anonimamente.cl] 
                antes de agendar tu sesión.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConsentimientoPage;
